import { useMemo } from 'react'

import styled from 'styled-components'

import { ProjectFloor } from '../../../../../api/types'
import { blue } from '../../../../../styles/colors'
import {
  CheckboxInput,
  CheckboxLabel,
  Checkmark,
} from '../../../../common/ViewSelector/components/PointGroupSelector/CheckNameDelete'
import { ViewSelector } from '../../../../common/ViewSelector/ViewSelector'

interface ScanHealthFloorSelectorProps {
  floors: ProjectFloor[]
  selectedFloorCodes: Set<string>
  onToggleFloor: (floorCode: string, checked: boolean) => void
  onSelectAll: () => void
  onDeselectAll: () => void
  expanded: boolean
  setExpanded: (value: boolean) => void
}

export const ScanHealthFloorSelector = ({
  floors,
  selectedFloorCodes,
  onToggleFloor,
  onSelectAll,
  onDeselectAll,
  expanded,
  setExpanded,
}: ScanHealthFloorSelectorProps) => {
  const sortedFloors = useMemo(
    () => [...floors].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [floors],
  )

  const allSelected =
    sortedFloors.length > 0 &&
    sortedFloors.every(floor => selectedFloorCodes.has(floor.floor_code))

  return (
    <ViewSelector
      title="View Selector"
      initialViewSelectorExpanded
      viewingExpanded={expanded}
      setViewingExpanded={setExpanded}
      maxHeight="calc(100vh - 155px)"
    >
      <FloorList>
        {sortedFloors.map(floor => {
          const checked = selectedFloorCodes.has(floor.floor_code)
          return (
            <FloorRow key={floor.floor_code}>
              <CheckboxLabel>
                <CheckboxInput
                  checked={checked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onToggleFloor(floor.floor_code, e.target.checked)
                  }
                />
                <Checkmark checked={checked} backgroundColor={blue} />
                <FloorName>{floor.name}</FloorName>
              </CheckboxLabel>
            </FloorRow>
          )
        })}
        <BulkActions>
          <BulkActionButton
            type="button"
            onClick={allSelected ? onDeselectAll : onSelectAll}
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </BulkActionButton>
        </BulkActions>
      </FloorList>
    </ViewSelector>
  )
}

const FloorList = styled.div`
  margin-top: 8px;
`

const FloorRow = styled.div`
  box-sizing: border-box;
  height: 36px;
  margin: 8px 0;
  padding: 0 8px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  display: flex;
  align-items: center;
`

const FloorName = styled.span`
  color: #686868;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const BulkActions = styled.div`
  margin-top: 4px;
  padding: 0 8px;
`

const BulkActionButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${blue};
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    opacity: 0.85;
  }
`
